import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import useChecklistItem from "./use-checklist-item";
import { ChecklistBlockConfigurationRecord } from "models/view-models/block-configurations/checklist-block-configuration-record";
import { useState } from "react";
import ChecklistItemNoteService from "utilities/services/checklist-item-notes/checklist-item-note-service";
import UsePageLimits from "pages/notebooks/use-page-limits";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";

export enum MergeChecklistItemEvent {
    MergeWithPrevious,
    MergeWithNext,
}

interface UseSplitOrMergeChecklistItems {
    handleMergeCurrentWithPrevious: (index: number) => Promise<void>;
    handleMergeCurrentWithNext: (index: number) => Promise<void>;
    handleSplitContentBetweenItems: (
        index: number,
        cursorPosition: number
    ) => Promise<void>;
    saveInProgress: boolean;
}

type ItemUpdater =
    | ChecklistItemRecord[]
    | ((prevItems: ChecklistItemRecord[]) => ChecklistItemRecord[]);

const useSplitOrMergeChecklistItems = (
    checklistBlockConfigurationId: number,
    items: ChecklistItemRecord[],
    onBlockSaving: () => void,
    onBlockSaved: (updatedBlock: any) => void,
    onItemsSaved: (updateItems: ItemUpdater) => void,
    block: ChecklistBlockConfigurationRecord,
    onMergeBlocked: () => void
): UseSplitOrMergeChecklistItems => {
    const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
    const { updateChecklistItem, createChecklistItem, deleteChecklistItem } =
        useChecklistItem();
    const { update: updateNote } = ChecklistItemNoteService.useUpdate();
    const { pageLimits } = UsePageLimits();

    const handleSplitContentBetweenItems = async (
        index: number,
        cursorPosition: number
    ) => {
        const currentItem = items[index];

        if (checkChecklistItemLimit()) {
            return;
        }

        const textBeforeCursor = currentItem.text.slice(0, cursorPosition);
        const textAfterCursor = currentItem.text.slice(cursorPosition);

        const updatedCurrentItem: ChecklistItemRecord = currentItem.with({
            text: textBeforeCursor,
        });

        onBlockSaving();
        setSaveInProgress(true);

        try {
            const pendingUpdate = updateChecklistItem(updatedCurrentItem);

            const pendingCreate = await createChecklistItem(
                checklistBlockConfigurationId,
                currentItem.id,
                textAfterCursor
            );

            const results = await Promise.all([pendingUpdate, pendingCreate]);
            const updatedItem = results[0];
            const createdItem = results[1];
            if (!createdItem) return;
            if (!updatedItem) return;

            const originalItem = updatedItem.with({ note: currentItem.note });

            onItemsSaved((prev) => {
                const newItems = [...prev];
                newItems[index] = originalItem;
                newItems.splice(index + 1, 0, createdItem);
                return newItems;
            });
            setSaveInProgress(false);

            setTimeout(() => {
                const element = document.getElementById(
                    `item-text-${index + 1}`
                ) as HTMLInputElement | null;
                element?.focus();
                element?.setSelectionRange(0, 0);
                onBlockSaved(block);
            }, 25);
        } catch (error) {
            console.error("Error splitting checklist item on Enter", error);
            setSaveInProgress(false);
            onBlockSaved(block);
        }
    };

    const handleMergeCurrentWithPrevious = async (index: number) => {
        if (index === 0) {
            return;
        }
        const currentItem = items[index];
        const previousItem = items[index - 1];
        const mergedText = (previousItem.text ?? "") + (currentItem.text ?? "");
        const updatePreviousItem = (currentItem.text ?? "") !== "";
        if (currentItem.note && previousItem.note) {
            onMergeBlocked();
            return;
        }
        const noteToMerge = currentItem.note ?? previousItem.note;

        const updatedPreviousItem = previousItem.with({
            text: mergedText,
            note: noteToMerge,
        });

        if (currentItem.note && previousItem.note) {
            onMergeBlocked();
            return;
        }

        onBlockSaving();

        try {
            if (updatePreviousItem) {
                await updateChecklistItem(
                    updatedPreviousItem as ChecklistItemRecord
                );
                if (noteToMerge) {
                    await updateNote(
                        noteToMerge.with({
                            checklistItemId: updatedPreviousItem.id,
                        }),
                        { id: noteToMerge.id! }
                    );
                }
            }

            onItemsSaved((prev) => {
                const newItems = [...prev];
                newItems.splice(index - 1, 2, updatedPreviousItem);
                return newItems;
            });

            onBlockSaved(block);
            //do not await here to speed up the process
            deleteChecklistItem(currentItem.id);
        } catch (error) {
            onBlockSaved(block);
            return;
        }

        setTimeout(() => {
            const element = document.getElementById(
                `item-text-${index - 1}`
            ) as HTMLInputElement | null;
            if (element) {
                element.focus();
                element.selectionStart = element.selectionEnd = (
                    previousItem.text ?? ""
                ).length;
            }
        }, 100);
    };

    const handleMergeCurrentWithNext = async (index: number) => {
        if (index < 0 || index >= items.length - 1) return;

        const currentItem = items[index];
        const nextItem = items[index + 1];
        const mergedText = (currentItem.text ?? "") + (nextItem.text ?? "");

        if (currentItem.note && nextItem.note) {
            onMergeBlocked();
            return;
        }

        const noteToMerge = currentItem.note ?? nextItem.note;
        const updatedCurrentItem = currentItem.with({
            text: mergedText,
            note: noteToMerge,
        });

        onBlockSaving();

        try {
            await updateChecklistItem(
                updatedCurrentItem as ChecklistItemRecord
            );
            await deleteChecklistItem(nextItem.id);
            if (noteToMerge) {
                await updateNote(
                    noteToMerge.with({
                        checklistItemId: updatedCurrentItem.id,
                    }),
                    { id: noteToMerge.id! }
                );
            }

            onItemsSaved((previousItems) => {
                const newItems = [...previousItems];
                newItems.splice(index, 2, updatedCurrentItem);
                return newItems;
            });
            onBlockSaved(block);
        } catch (error) {
            console.error("Error merging items", error);

            onBlockSaved(block);
        }

        setTimeout(() => {
            const element = document.getElementById(
                `item-text-${index}`
            ) as HTMLInputElement | null;
            if (element) {
                element.focus();
                element.selectionStart = element.selectionEnd = (
                    currentItem.text ?? ""
                ).length;
            }
        }, 100);
    };

    const checkChecklistItemLimit = () => {
        if (
            pageLimits?.checklistItemsLimit &&
            items.length >= pageLimits.checklistItemsLimit
        ) {
            ToastManager.warn(t("exceedsChecklistItemLimit"));
            return true;
        }
        return false;
    };

    return {
        handleMergeCurrentWithPrevious,
        handleMergeCurrentWithNext,
        handleSplitContentBetweenItems,
        saveInProgress,
    };
};
export default useSplitOrMergeChecklistItems;
